import type { EmailTemplates, WidgetSettings } from './branding.js';
import type { Locale } from '../i18n/index.js';

/**
 * Ready-made looks for the booking page and the guest emails. Applying one
 * replaces the look (colours, fonts, texts, field choices, email wording) and
 * never touches the restaurant's own data: name, address, logo and contact
 * details stay as they are.
 *
 * Pure data, shared by the dashboard (one-click apply) and the provisioning
 * command (start a new customer with a look already in place).
 */
export interface LookPreset {
  id: string;
  name: Record<Locale, string>;
  description: Record<Locale, string>;
  accentColor: string;
  widget: Partial<WidgetSettings>;
  emailTemplates: EmailTemplates;
}

const warm: EmailTemplates = {
  confirmation: {
    en: { subject: 'Your table at {restaurant} is booked', lead: 'Thank you, {guest}. We have saved a table for {party} on {when} and look forward to welcoming you.' },
    de: { subject: 'Ihr Tisch bei {restaurant} ist reserviert', lead: 'Vielen Dank, {guest}. Wir haben einen Tisch für {party} am {when} für Sie reserviert und freuen uns auf Ihren Besuch.' },
  },
  reminder: {
    en: { subject: 'See you soon at {restaurant}', lead: 'A friendly reminder, {guest}: your table for {party} is waiting on {when}.' },
    de: { subject: 'Bis bald im {restaurant}', lead: 'Eine freundliche Erinnerung, {guest}: Ihr Tisch für {party} wartet am {when} auf Sie.' },
  },
};

const formal: EmailTemplates = {
  confirmation: {
    en: { subject: 'Reservation confirmed: {restaurant}', lead: 'Dear {guest}, your reservation for {party} on {when} is confirmed. Reference: {code}.' },
    de: { subject: 'Reservierung bestätigt: {restaurant}', lead: 'Sehr geehrte(r) {guest}, Ihre Reservierung für {party} am {when} ist bestätigt. Referenz: {code}.' },
  },
  reminder: {
    en: { subject: 'Your reservation at {restaurant}', lead: 'Dear {guest}, we would like to remind you of your reservation for {party} on {when}.' },
    de: { subject: 'Ihre Reservierung im {restaurant}', lead: 'Sehr geehrte(r) {guest}, wir erinnern Sie an Ihre Reservierung für {party} am {when}.' },
  },
};

const casual: EmailTemplates = {
  confirmation: {
    en: { subject: "You're in! Table at {restaurant}", lead: "Great, {guest}! You're booked for {party} on {when}. Can't wait to see you." },
    de: { subject: 'Das passt! Ihr Tisch bei {restaurant}', lead: 'Prima, {guest}! Sie sind für {party} am {when} eingetragen. Wir freuen uns auf Sie.' },
  },
  reminder: {
    en: { subject: 'Tonight? Your table at {restaurant}', lead: 'Quick reminder, {guest}: {party} at {when}. See you there!' },
    de: { subject: 'Bald geht es los: {restaurant}', lead: 'Kurze Erinnerung, {guest}: {party} am {when}. Bis gleich!' },
  },
};

export const LOOK_PRESETS: LookPreset[] = [
  {
    id: 'classic-bistro',
    name: { en: 'Classic bistro', de: 'Klassisches Bistro' },
    description: { en: 'Warm red, serif headings, relaxed and traditional.', de: 'Warmes Rot, Serifenschrift, entspannt und traditionell.' },
    accentColor: '#9b2c2c',
    widget: {
      theme: 'light', background: '#fffaf5', panel: '#f6ece2', textColor: '#2b1d17', font: 'serif', radius: 10,
      width: 'normal', buttonStyle: 'solid', headerAlign: 'left', showLogo: true, showName: true,
    },
    emailTemplates: warm,
  },
  {
    id: 'modern-minimal',
    name: { en: 'Modern minimal', de: 'Modern minimal' },
    description: { en: 'Clean white, thin outlines, system font.', de: 'Klares Weiß, feine Linien, Systemschrift.' },
    accentColor: '#111827',
    widget: {
      theme: 'light', background: null, panel: null, textColor: null, font: 'system', radius: 4,
      width: 'normal', buttonStyle: 'outline', headerAlign: 'left', showLogo: true, showName: true,
    },
    emailTemplates: casual,
  },
  {
    id: 'elegant-dark',
    name: { en: 'Elegant dark', de: 'Elegant dunkel' },
    description: { en: 'Dark page with a gold accent for evening venues.', de: 'Dunkle Seite mit goldenem Akzent für Abendlokale.' },
    accentColor: '#c9a24b',
    widget: {
      theme: 'dark', background: '#14110f', panel: '#201b17', textColor: '#f3ece2', font: 'serif', radius: 6,
      width: 'normal', buttonStyle: 'solid', headerAlign: 'center', showLogo: true, showName: true,
    },
    emailTemplates: formal,
  },
  {
    id: 'fresh-friendly',
    name: { en: 'Fresh & friendly', de: 'Frisch & freundlich' },
    description: { en: 'Green, rounded corners, relaxed wording for cafés.', de: 'Grün, runde Ecken, lockere Texte für Cafés.' },
    accentColor: '#2f855a',
    widget: {
      theme: 'light', background: '#f7fbf8', panel: '#e8f3ec', textColor: '#1d3a2b', font: 'rounded', radius: 20,
      width: 'normal', buttonStyle: 'solid', headerAlign: 'center', showLogo: true, showName: true,
    },
    emailTemplates: casual,
  },
  {
    id: 'fine-dining',
    name: { en: 'Fine dining', de: 'Gehobene Küche' },
    description: { en: 'Narrow, quiet and formal; phone number required.', de: 'Schmal, ruhig und förmlich; Telefonnummer ist Pflicht.' },
    accentColor: '#1f2a44',
    widget: {
      theme: 'light', background: '#ffffff', panel: '#f4f5f8', textColor: '#1b2230', font: 'serif', radius: 2,
      width: 'narrow', buttonStyle: 'solid', headerAlign: 'center', showLogo: true, showName: true,
      fields: { phone: 'required', occasion: true, request: true, marketing: false, sms: false, area: true },
    },
    emailTemplates: formal,
  },
  {
    id: 'pub-and-bar',
    name: { en: 'Pub & bar', de: 'Pub & Bar' },
    description: { en: 'Dark, bold and compact, with outdoor choice up front.', de: 'Dunkel, kräftig und kompakt, mit Wahl des Außenbereichs.' },
    accentColor: '#e07a1f',
    widget: {
      theme: 'dark', background: null, panel: null, textColor: null, font: 'system', radius: 8,
      width: 'narrow', buttonStyle: 'solid', headerAlign: 'left', showLogo: true, showName: true,
      fields: { phone: 'optional', occasion: false, request: true, marketing: false, sms: false, area: true },
    },
    emailTemplates: casual,
  },
];

export const findPreset = (id: string) => LOOK_PRESETS.find((p) => p.id === id) ?? null;

/**
 * The part of a branding block that applying a look replaces. Everything else
 * (logo, contact details, closing notes) is kept by the caller.
 */
export function lookOf(p: LookPreset, current: WidgetSettings): { accentColor: string; widget: WidgetSettings } {
  const keepTexts = { title: current.title, intro: current.intro, confirmLabel: current.confirmLabel, thanks: current.thanks };
  const base = { ...current };
  return {
    accentColor: p.accentColor,
    widget: {
      ...base,
      ...p.widget,
      ...keepTexts,
      fields: { ...current.fields, ...(p.widget.fields ?? {}) },
      // A look never carries someone else's CSS, and clears the old one so nothing fights the new look.
      customCss: '',
    },
  };
}
